import { useMemo, useState } from "react";
import {
  Sparkles,
  ArrowRight,
  GraduationCap,
  Code2,
  Landmark,
  Briefcase,
  Palette,
  ChevronDown,
  Compass,
} from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * CourseDiscovery — guided "I don't know what to learn" funnel.
 *
 * 2 cascading dropdowns (Category → Subcategory) followed by a strip of
 * 6–10 hot/popular course recommendations. Clicking any chip submits
 * the full goal into the parent's path-generation flow. Lives between
 * the hero search and the language/tone selector on the Hub page.
 *
 * Props:
 *   onPickCourse(goalString) — called when the learner clicks a hot
 *     recommendation. Expected to populate the search input + submit.
 *   busy — bool, disables clicks while a generation is mid-flight.
 */

// --- CATALOG -----------------------------------------------------------
// Each leaf is a curated array of high-intent course goals. Order is
// "most-popular first" so the head of the list is what we surface in the
// "Hot picks" preview row before any subcategory is chosen.
const CATALOG = {
  Academic: {
    Icon: GraduationCap,
    accent: "#b9a8ff",
    tint: "rgba(185,168,255,.10)",
    border: "rgba(185,168,255,.32)",
    sub: {
      "Exam Prep": [
        "Crack SAT Math in 6 weeks",
        "GMAT Quant mastery — every question type",
        "NEET Biology — full syllabus crash plan",
        "JEE Advanced Physics problem-solving",
        "GRE Verbal — vocabulary + reading comp",
        "CAT logical reasoning + DI mastery",
        "UPSC CSE prelims — polity + history",
        "MCAT — biochemistry deep dive",
        "LSAT logical games strategy",
        "IELTS Academic — band 8 writing",
      ],
      STEM: [
        "Calculus I — limits, derivatives, integrals",
        "Linear algebra for ML",
        "Probability & statistics for data science",
        "Organic chemistry — name reactions",
        "Cell biology fundamentals",
        "Physics: classical mechanics",
        "Discrete mathematics for CS",
      ],
      Humanities: [
        "Modern philosophy 1600–1900",
        "World history: 1900–2000 in 8 hours",
        "Microeconomics fundamentals",
        "Intro to psychology — major schools",
        "Sociology 101 — key thinkers",
        "Comparative political systems",
        "Literary theory & criticism",
      ],
    },
  },
  Tech: {
    Icon: Code2,
    accent: "#00FF9D",
    tint: "rgba(0,255,157,.10)",
    border: "rgba(0,255,157,.32)",
    sub: {
      "Web Development": [
        "Master React Server Components in 2026",
        "Next.js 15 — App Router fundamentals",
        "Frontend performance for slow networks",
        "Web security — OWASP top 10",
        "Build a REST API in FastAPI",
        "GraphQL with Apollo from scratch",
      ],
      "AI / Machine Learning": [
        "Build agentic AI workflows with LangGraph",
        "Production RAG systems on Postgres",
        "Fine-tune open-source LLMs (LoRA/QLoRA)",
        "Prompt engineering for product teams",
        "Computer vision with PyTorch",
        "MLOps — deploy + monitor models",
      ],
      "Backend / Infra": [
        "Postgres performance tuning for indie SaaS",
        "Kubernetes for application developers",
        "Distributed systems — CAP, consensus, sharding",
        "System design interview prep",
        "Caching strategies — Redis + CDN",
        "Microservices vs monolith — when to split",
      ],
      Mobile: [
        "iOS 18 SwiftUI for a polished MVP",
        "Android with Kotlin & Jetpack Compose",
        "React Native — ship to both stores",
        "Flutter from zero to App Store",
      ],
      Data: [
        "SQL mastery in 4 weekends",
        "dbt + Snowflake for modern analytics",
        "Spark for batch + streaming",
        "Data engineering fundamentals",
      ],
    },
  },
  Finance: {
    Icon: Landmark,
    accent: "#FFD86B",
    tint: "rgba(255,216,107,.10)",
    border: "rgba(255,216,107,.32)",
    sub: {
      Investing: [
        "Index fund investing for beginners",
        "Stock valuation — DCF + multiples",
        "Options trading — calls, puts, spreads",
        "Crypto fundamentals — BTC, ETH, DeFi",
        "Real estate investing — rental cash flow",
        "Bond markets and yield curves",
      ],
      "Personal Finance": [
        "Budgeting that actually sticks",
        "Tax planning for salaried professionals",
        "Retirement strategy — 401k / IRA / EPF",
        "Debt-payoff strategy: snowball vs avalanche",
        "FIRE movement — math and trade-offs",
      ],
      "Corporate Finance": [
        "How to read a startup balance sheet in 30 minutes",
        "DCF modeling end-to-end",
        "M&A strategy & deal mechanics",
        "Cash-flow analysis for SaaS",
        "IPO process — S-1 to listing day",
      ],
      Markets: [
        "Macroeconomics: the IS-LM model in 60 minutes",
        "FX markets — carry, hedging, central banks",
        "Commodities — oil, gold, agri",
        "Central banks and interest rates",
      ],
    },
  },
  Business: {
    Icon: Briefcase,
    accent: "#0044FF",
    tint: "rgba(0,68,255,.10)",
    border: "rgba(0,68,255,.32)",
    sub: {
      Marketing: [
        "Building a personal brand on LinkedIn in 2026",
        "Performance marketing — Google + Meta ads",
        "Content marketing strategy that compounds",
        "SEO fundamentals for indie founders",
        "Email marketing — segmentation + lifecycle",
      ],
      Sales: [
        "B2B sales discovery framework — MEDDPICC explained",
        "Cold outreach that gets replies",
        "Negotiation playbook for first-time managers",
        "Closing techniques for enterprise deals",
      ],
      Leadership: [
        "Manager 101 — your first 90 days",
        "Hiring frameworks for early-stage teams",
        "Running effective 1:1s",
        "Performance reviews without the drama",
      ],
      Strategy: [
        "Bootstrapped SaaS pricing strategy for early founders",
        "Product strategy — Jobs To Be Done",
        "Go-to-market for B2B startups",
        "Competitive analysis frameworks",
      ],
      Entrepreneurship: [
        "Idea validation in 30 days",
        "Fundraising — seed to Series A",
        "Finding product-market fit",
        "Scaling from 1 to 10 employees",
      ],
    },
  },
  Other: {
    Icon: Palette,
    accent: "#ffb067",
    tint: "rgba(255,176,103,.10)",
    border: "rgba(255,176,103,.32)",
    sub: {
      "Creative & Crafts": [
        "Beginner's guide to film photography on a 35mm SLR",
        "Watercolor painting for absolute beginners",
        "Hand-lettering & modern calligraphy",
        "Pottery wheel basics in 6 weekends",
      ],
      Wellness: [
        "Meditation — a 30-day practical practice",
        "Yoga for desk workers",
        "Nutrition basics without fad diets",
        "Sleep optimization — chronotype + light",
        "Habit building using BJ Fogg's model",
      ],
      Cooking: [
        "Master sourdough bread in 6 weekends",
        "Learn to brew espresso at home like a third-wave cafe",
        "Authentic Indian curries from scratch",
        "Sushi at home — rice, fish, knife work",
        "BBQ — low-and-slow ribs, brisket, pork",
        "French pastry fundamentals",
      ],
      Music: [
        "Guitar fundamentals — first 12 chords",
        "Piano sight-reading for adults",
        "Music theory in plain English",
        "Vocal technique for beginners",
        "Music production with Logic Pro",
      ],
      Languages: [
        "Spanish for travelers — A1 in 4 weeks",
        "Conversational Mandarin essentials",
        "Japanese basics for anime fans",
        "French A1 — speak from week 1",
      ],
      "Sports & Outdoors": [
        "Running for beginners — 5K plan",
        "Strength training fundamentals",
        "Cycling — fit, form, and first century",
        "Rock climbing technique for beginners",
      ],
      Hobbies: [
        "Bonsai care for the absolute beginner",
        "Tarot reading fundamentals for a personal practice",
        "Chess openings for club players",
      ],
    },
  },
};

const CAT_ORDER = ["Academic", "Tech", "Finance", "Business", "Other"];

export default function CourseDiscovery({ onPickCourse, busy, isFree = false, isLimitless = false }) {
  const { t } = useT();
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");

  const subOptions = useMemo(
    () => (category ? Object.keys(CATALOG[category].sub) : []),
    [category],
  );

  // What to show in the hot-topics row:
  //   - both picked  → that subcategory's curated list
  //   - only category → show the top 2 from each subcategory under it
  //   - nothing picked → show 8 evergreen popular goals across categories
  const recommendations = useMemo(() => {
    if (category && subcategory) return CATALOG[category].sub[subcategory] || [];
    if (category) {
      const all = Object.values(CATALOG[category].sub).flatMap((arr) => arr.slice(0, 2));
      return all.slice(0, 10);
    }
    // Curated cross-category trending picks (mirrors what we'd put on a
    // "for you" homepage). Pulls one banger from each category so the
    // user immediately sees breadth.
    return [
      "Crack SAT Math in 6 weeks",
      "Build agentic AI workflows with LangGraph",
      "Index fund investing for beginners",
      "Building a personal brand on LinkedIn in 2026",
      "Master sourdough bread in 6 weekends",
      "How to read a startup balance sheet in 30 minutes",
      "Master React Server Components in 2026",
      "Meditation — a 30-day practical practice",
    ];
  }, [category, subcategory]);

  const meta = category ? CATALOG[category] : null;
  const Accent = meta?.Icon || Compass;

  return (
    <section
      className="mt-10 md:mt-14 max-w-4xl mx-auto"
      data-testid="course-discovery-section"
    >
      <div
        className="glass !rounded-2xl p-6 md:p-8 relative overflow-hidden"
        style={{
          background: meta
            ? `linear-gradient(180deg, ${meta.tint} 0%, rgba(255,255,255,0.02) 100%)`
            : undefined,
        }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-5 md:mb-6">
          <div
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: meta ? `${meta.tint}` : "rgba(0,255,157,0.10)",
              border: `1px solid ${meta?.border || "rgba(0,255,157,0.30)"}`,
              color: meta?.accent || "#00FF9D",
            }}
          >
            <Accent size={18} />
          </div>
          <div className="min-w-0">
            <div className="label-eyebrow mb-1.5 flex items-center gap-2">
              <Sparkles size={11} className="text-[#00FF9D]" />
              {t("discover_eyebrow")}
            </div>
            <h2 className="font-display font-semibold tracking-tight text-xl md:text-2xl leading-tight">
              {t("discover_title")}
            </h2>
            <p className="text-white/55 text-sm mt-1.5 max-w-xl leading-relaxed">
              {t("discover_sub")}
            </p>
          </div>
        </div>

        {/* Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <DropDown
            label={t("discover_pick_category")}
            value={category}
            options={CAT_ORDER.map((c) => ({ value: c, label: c }))}
            placeholder={t("discover_pick_category_placeholder")}
            onChange={(v) => {
              setCategory(v);
              setSubcategory("");
            }}
            testid="discover-category"
          />
          <DropDown
            label={t("discover_pick_subcategory")}
            value={subcategory}
            options={subOptions.map((s) => ({ value: s, label: s }))}
            placeholder={
              category
                ? t("discover_pick_subcategory_placeholder")
                : t("discover_pick_subcategory_disabled")
            }
            disabled={!category}
            onChange={setSubcategory}
            testid="discover-subcategory"
          />
        </div>

        {/* Hot topic chips */}
        <div className="mt-1">
          <div className="text-[11px] font-display font-semibold tracking-widest uppercase text-white/55 mb-3 flex items-center gap-2">
            <span
              className="h-1.5 w-1.5 rounded-full animate-pulse"
              style={{ background: meta?.accent || "#00FF9D" }}
            />
            {category && subcategory
              ? t("discover_hot_in_subcategory").replace("{sub}", subcategory)
              : category
                ? t("discover_hot_in_category").replace("{cat}", category)
                : t("discover_hot_trending")}
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="discover-recommendations"
          >
            {isFree && (
              <div
                className="w-full -mb-1 flex items-center gap-2 text-[11.5px] text-[#9bffcc]"
                data-testid="discover-free-banner"
              >
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#00FF9D]/35 bg-[#00FF9D]/[0.08] font-display font-semibold tracking-wide">
                  <Sparkles size={10} />
                  {isLimitless ? t("discover_free_limitless") : t("discover_free_credits")}
                </span>
                <span className="text-white/55">{t("discover_free_hint")}</span>
              </div>
            )}
            {recommendations.map((goal) => (
              <button
                key={goal}
                type="button"
                disabled={busy}
                onClick={() => onPickCourse?.(goal)}
                className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border bg-white/[0.03] hover:bg-white/[0.07] text-[13px] text-white/85 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed text-left min-w-0 max-w-full"
                style={{
                  borderColor: meta?.border || "rgba(255,255,255,0.10)",
                }}
                data-testid={`discover-pick-${goal.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                title={t("discover_chip_hint")}
              >
                <span className="truncate">{goal}</span>
                {isFree && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-[2px] rounded-md text-[9.5px] font-display font-bold tracking-widest uppercase bg-[#00FF9D]/15 text-[#9bffcc] border border-[#00FF9D]/30"
                    data-testid="discover-pick-free-pill"
                  >
                    {isLimitless ? "FREE" : "1 CR"}
                  </span>
                )}
                <ArrowRight
                  size={12}
                  className="shrink-0 opacity-60 group-hover:opacity-100 transition"
                  style={{ color: meta?.accent || "#00FF9D" }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Helpers ----------------------------------------------------------
function DropDown({ label, value, options, placeholder, disabled, onChange, testid }) {
  return (
    <label
      className={`block ${disabled ? "opacity-55" : ""}`}
      data-testid={`${testid}-wrap`}
    >
      <span className="block text-[11px] font-display font-semibold tracking-widest uppercase text-white/45 mb-1.5">
        {label}
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none bg-black/40 border border-white/12 rounded-xl px-3.5 py-2.5 pr-9 text-sm text-white/90 focus:outline-none focus:border-[#0044FF]/60 disabled:cursor-not-allowed"
          data-testid={testid}
        >
          <option value="" className="bg-[#0B0E14]">
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="bg-[#0B0E14]">
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/45"
        />
      </div>
    </label>
  );
}
